import { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import KnowledgeBaseNavItem from "./KnowledgeBaseNavItem";
import { 
  LayoutDashboard, 
  Phone, 
  Languages,
  Video, 
  MessageSquare,
  Smartphone,
  Users, 
  History, 
  CalendarDays, 
  Settings,
  LogOut,
  Menu,
  X,
  PhoneCall,
  ShieldCheck,
  Shield,
  Sparkles,
  Voicemail,
  Activity,
  BarChart3,
  Calendar,
  Building2,
  Heart,
  GraduationCap,
  Printer,
  Target,
  ListTodo,
  FileText,
  Archive,
  HeartPulse,
  KeyRound,
  Plus,
  Search,
  Wrench,
  Inbox,
  HelpCircle,
  Briefcase,
  Sun,
  Bot,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { RainbowDashboardIcon, RainbowBriefcaseIcon } from "./RainbowIcon";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { getInitials, getStatusColor, formatPhoneNumber } from "../../lib/utils";
import { cn, fileUrl } from "../../lib/utils";
import { adminAPI, authAPI } from "../../services/api";
import { useCounts } from "../../context/CountsContext";
import { setFaviconBadge, unlockAudio, playNotificationSound } from "../../lib/notifications";
import { isTeamDomain, canAccessComms } from "../../lib/domainMode";
import NotificationBell from "./NotificationBell";
import HoldingCallersBadge from "./HoldingCallersBadge";
import { useCall } from "./ActiveCallOverlay";
import { PhoneOff } from "lucide-react";

// ── Nav order + palette chosen by Dawn (2026-02-14). Colors below are
// per-item requests: rainbow for Dashboard + Projects (rendered as
// inline SVG with gradient defs via RainbowIcon), neon greens/purples/
// blues use explicit hex to stand out against the navy sidebar.
// `iconSrc` is only used for the two custom PNG icons — everything else
// stays on the lucide component in `icon` for crisp scaling.
const navItems = [
  { path: "/dashboard",         icon: LayoutDashboard, label: "Dashboard",        color: "text-white", rainbow: "dashboard" },
  { path: "/phone",             icon: Phone,           label: "Phone",            color: "text-blue-400" },
  { path: "/interpreter-requests", icon: Languages,    label: "Interpreter Requests", color: "text-violet-400" },
  { path: "/messages",          icon: MessageSquare,   label: "Team Chat",        color: "text-yellow-300", isIM: true },
  { path: "/video",             icon: Video,           label: "Team Meetings",    color: "text-orange-400" },
  { path: "/fax",               icon: Printer,         label: "Fax",              color: "text-[#39FF14]" },
  { path: "/chat",              icon: Bot,             label: "Live Website Chat", color: "text-[#BF00FF]" },
  { path: "/calendar",          icon: CalendarDays,    label: "Calendar",         color: "text-red-500" },
  { path: "/work",              icon: Briefcase,       label: "Work",             color: "text-white", rainbow: "briefcase" },
  { path: "/notepad",           icon: FileText,        label: "Notepad",          color: "text-yellow-300" },
  { path: "/tools",             icon: Wrench,          label: "Tools",            color: "text-green-500" },
  { path: "/tools/bgc-statute-check", icon: ShieldCheck, label: "DHS 12 Checker",  color: "text-blue-400", nested: true },
  { path: "/contacts",          icon: Users,           label: "Contacts",         color: "text-blue-400" },
  { path: "/documents",         iconSrc: "/nav-icons/docs-shield.png", label: "Documents", color: "text-purple-400" },
  { path: "/callbacks",         icon: Calendar,        label: "Callbacks",        color: "text-orange-400" },
  { path: "/communications-log", icon: Archive,        label: "Comms Log",        color: "text-blue-400" },
  { path: "/comms",             icon: Inbox,           label: "Comms Inbox",      color: "text-white" },
  { path: "/comms/unassigned",  icon: HelpCircle,      label: "Unassigned",       color: "text-yellow-300" },
  { path: "/settings",          icon: Settings,        label: "Settings",         color: "text-slate-400" },
];

const adminNavItems = [
  // Setup Wizard as the very first item — where a new admin lands
  // when they need to reconfigure or a teammate joins. Uses the
  // Sparkles icon so it feels welcoming, not another shield/lock.
  { path: "/admin/setup", icon: Sparkles, label: "Setup", color: "text-white/60", activeColor: "text-olive-500", bgColor: "bg-olive-50" },
  { path: "/agents", icon: Activity, label: "Agent Dashboard", color: "text-white/60", activeColor: "text-olive-500", bgColor: "bg-olive-50" },
  { path: "/analytics", icon: BarChart3, label: "Analytics", color: "text-white/60", activeColor: "text-olive-500", bgColor: "bg-olive-50" },
  { path: "/analytics/live-calls", icon: PhoneCall, label: "Live Calls", color: "text-white/60", activeColor: "text-olive-500", bgColor: "bg-olive-50" },
  { path: "/admin", icon: ShieldCheck, label: "Admin Panel", color: "text-white/60", activeColor: "text-olive-500", bgColor: "bg-olive-50" },
  { path: "/admin/integration-health", icon: Activity, label: "Integration Health", color: "text-white/60", activeColor: "text-olive-500", bgColor: "bg-olive-50" },
  { path: "/permissions", icon: KeyRound, label: "Permissions", color: "text-white/60", activeColor: "text-olive-500", bgColor: "bg-olive-50" },
  { path: "/audit-log", icon: History, label: "Audit Log", color: "text-white/60", activeColor: "text-olive-500", bgColor: "bg-olive-50" },
  // Hardening surfaces added iter 472 — the middleware-powered audit
  // log (every PHI mutation) and the K8s-level System Health dashboard
  // both need to be one click from anywhere so ops can find them
  // during an incident. Labels are prefixed "System" so they don't
  // collide with the pre-existing healthcare-scoped Audit Log and
  // Integration Health entries above.
  { path: "/admin/audit-log", icon: Shield, label: "Audit Trail", color: "text-white/60", activeColor: "text-olive-500", bgColor: "bg-olive-50" },
  { path: "/admin/health", icon: HeartPulse, label: "System Health", color: "text-white/60", activeColor: "text-olive-500", bgColor: "bg-olive-50" },
];

const statusOptions = [
  { value: "available", label: "Available", color: "bg-emerald-500" },
  { value: "busy", label: "Busy", color: "bg-red-500" },
  { value: "away", label: "Away", color: "bg-amber-500" },
  { value: "offline", label: "Offline", color: "bg-slate-400" },
];

// Company theme mapping
const COMPANY_THEMES = {
  bylyngo: { icon: Building2, color: "bg-blue-500", text: "text-blue-400" },
  avida: { icon: Heart, color: "bg-yellow-500", text: "text-yellow-400" },
  institute: { icon: GraduationCap, color: "bg-red-500", text: "text-red-400" }
};

const getCompanyTheme = (name) => {
  if (!name) return COMPANY_THEMES.bylyngo;
  const n = name.toLowerCase();
  if (n.includes("institute")) return COMPANY_THEMES.institute;
  if (n.includes("avida")) return COMPANY_THEMES.avida;
  return COMPANY_THEMES.bylyngo;
};

export const Sidebar = ({ isOpen, onToggle, collapsed = false }) => {
  const location = useLocation();
  const { user, logout, updateStatus } = useAuth();
  // Live softphone registration state → drives the Phone nav status pip.
  const phoneReady = !!useCall()?.deviceReady;
  const showComms = canAccessComms(user?.role);
  // Simple-phone employees get a minimal sidebar (just their phone + settings).
  const simple = !!user?.simple_phone;
  
  // Unread/notification counts come from the shared CountsProvider — ONE
  // app-wide poller that pauses when the tab is hidden — instead of Sidebar
  // running its own five intervals on every page.
  const { unreadCounts, totalUnread, imUnread, imMentions, missedCalls, vmCount, taskCounts } = useCounts();
  const [companies, setCompanies] = useState([]);

  // Company list rarely changes — fetch ONCE on mount instead of every 10s.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await adminAPI.getCompanies();
        if (!active) return;
        const mainCompanies = (res.data || [])
          .filter(c => !c.name?.toLowerCase().includes("waleska"))
          .sort((a, b) => {
            const priority = (name) => {
              if (!name) return 99;
              const n = name.toLowerCase();
              if (n.includes("avida")) return 1;
              if (n.includes("bylyngo interpreting")) return 2;
              if (n.includes("institute")) return 3;
              return 99;
            };
            return priority(a.name) - priority(b.name);
          })
          .slice(0, 3);
        setCompanies(mainCompanies);
      } catch (err) {
        // silent
      }
    })();
    return () => { active = false; };
  }, []);

  // Favicon red dot: show when any unread notifications exist
  useEffect(() => {
    const hasUnread = totalUnread > 0 || imUnread > 0 || missedCalls > 0 || vmCount > 0;
    setFaviconBadge(hasUnread);
    const totalAll = totalUnread + imUnread + missedCalls + vmCount;
    const appName = isTeamDomain() ? "Team Avida" : "Avida Health";
    document.title = totalAll > 0 ? `(${totalAll}) ${appName}` : appName;
  }, [totalUnread, imUnread, missedCalls, vmCount]);

  // Play notification sound when counts increase (new message/call arrived)
  const prevImUnreadRef = useRef(null);
  const prevSmsUnreadRef = useRef(null);
  const prevVmCountRef = useRef(null);
  const prevMissedRef = useRef(null);

  useEffect(() => {
    if (prevImUnreadRef.current === null) { prevImUnreadRef.current = imUnread; return; }
    if (imUnread > prevImUnreadRef.current && !window.location.pathname.startsWith("/messages")) {
      playNotificationSound();
    }
    prevImUnreadRef.current = imUnread;
  }, [imUnread]);

  useEffect(() => {
    if (prevSmsUnreadRef.current === null) { prevSmsUnreadRef.current = totalUnread; return; }
    if (totalUnread > prevSmsUnreadRef.current && !window.location.pathname.startsWith("/phone")) {
      playNotificationSound();
    }
    prevSmsUnreadRef.current = totalUnread;
  }, [totalUnread]);

  useEffect(() => {
    if (prevVmCountRef.current === null) { prevVmCountRef.current = vmCount; return; }
    if (vmCount > prevVmCountRef.current) {
      playNotificationSound();
    }
    prevVmCountRef.current = vmCount;
  }, [vmCount]);

  useEffect(() => {
    if (prevMissedRef.current === null) { prevMissedRef.current = missedCalls; return; }
    if (missedCalls > prevMissedRef.current) {
      playNotificationSound();
    }
    prevMissedRef.current = missedCalls;
  }, [missedCalls]);

  // Unlock audio on first click (browser autoplay policy)
  useEffect(() => {
    const handleInteraction = () => unlockAudio();
    document.addEventListener("click", handleInteraction, { once: true });
    document.addEventListener("keydown", handleInteraction, { once: true });
    // Request browser notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
    return () => {
      document.removeEventListener("click", handleInteraction);
      document.removeEventListener("keydown", handleInteraction);
    };
  }, []);

  // PCWs on app domain: hide the main sidebar entirely (after all hooks)
  if (!showComms) return null;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-30 md:hidden"
          onClick={onToggle}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed left-0 top-0 h-full bg-navy-600 flex flex-col z-40 transition-all duration-300 shadow-lg",
          collapsed ? "w-16" : "w-64",
          isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
        data-testid="sidebar"
      >
        {/* Header */}
        <div className={cn("border-b border-navy-500", collapsed ? "p-3" : "p-6")}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logos/avida-logo.png" alt={isTeamDomain() ? "Team Avida" : "Avida Health"} className={cn("rounded-xl shadow-lg", collapsed ? "w-9 h-9" : "w-10 h-10")} />
              {!collapsed && (
                <div>
                  <h1 className="text-lg font-bold text-white">{isTeamDomain() ? "Team Avida" : "Avida Health"}</h1>
                  <p className="text-xs text-yellow-300 font-semibold">{isTeamDomain() ? "Team & Care Management" : "Healthcare Operations"}</p>
                </div>
              )}
            </div>
            {!collapsed && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden text-navy-200"
                onClick={onToggle}
                data-testid="sidebar-close-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className={cn("flex-1 overflow-y-auto", collapsed ? "py-2" : "py-4")} data-testid="sidebar-nav">
          {/* Quick actions: Search (hidden for simple-phone users) */}
          {!simple && (
          <div className={cn("grid gap-2 mb-3", collapsed ? "px-2 grid-cols-1" : "px-3 grid-cols-1")}>
            <button
              onClick={() => window.__avidaQuickActions?.openSearch?.()}
              title="Search (Cmd+K)"
              className={cn(
                "flex items-center justify-center gap-1.5 rounded-lg font-semibold text-xs transition-all border",
                collapsed ? "h-10 w-10 mx-auto" : "py-2 px-3"
              )}
              style={{ background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.18)", color: "white" }}
              data-testid="sidebar-search-btn"
            >
              <Search className="w-4 h-4" />
              {!collapsed && <span>Search</span>}
            </button>
          </div>
          )}

          {/* Avida Health Hub - Top of menu */}
          {!simple && (() => {
            const onTeamDomain = isTeamDomain();
            const appUrl = "https://app.avidahealth.org";
            return (
              <div className="relative group/hub">
                {onTeamDomain ? (
                  <a
                    href={appUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={collapsed ? "Avida Health Hub" : undefined}
                    className={cn(
                      "flex items-center transition-all duration-200 group",
                      collapsed ? "justify-center mx-2 px-0 py-2.5 rounded-xl mb-2" : "gap-3 mx-3 px-4 py-3 text-sm font-bold rounded-xl mb-3"
                    )}
                    style={{ background: "#0866FF", color: "#FFD700" }}
                    data-testid="nav-healthcare-hub"
                  >
                    <HeartPulse className="w-5 h-5 text-[#FFD700]" strokeWidth={2} />
                    {!collapsed && <span>Avida Health Hub</span>}
                  </a>
                ) : (
                  <NavLink
                    to="/healthcare"
                    title={collapsed ? "Avida Health Hub" : undefined}
                    className={cn(
                      "flex items-center transition-all duration-200 group",
                      collapsed ? "justify-center mx-2 px-0 py-2.5 rounded-xl mb-2" : "gap-3 mx-3 px-4 py-3 text-sm font-bold rounded-xl mb-3"
                    )}
                    style={{ background: "#0866FF", color: "#FFD700" }}
                    data-testid="nav-healthcare-hub"
                  >
                    <HeartPulse className="w-5 h-5 text-[#FFD700]" strokeWidth={2} />
                    {!collapsed && <span>Avida Health Hub</span>}
                  </NavLink>
                )}
              </div>
            );
          })()}

          {/* Company badge for employees */}
          {!collapsed && user?.role === "user" && user?.company_name && (
            <div className="mx-4 mb-3 px-3 py-2 bg-navy-700 rounded-lg">
              <p className="text-[10px] font-semibold text-navy-200 uppercase tracking-wider">Company</p>
              <p className="text-sm font-medium text-white truncate">{user.company_name}</p>
            </div>
          )}
          {navItems
            .filter((item) => {
              if (simple) return ["/phone", "/settings"].includes(item.path);
              if (item.path === "/sms" && user?.role === "user" && !user?.sms_enabled) return false;
              if (item.path === "/chat" && user?.role === "user") return false;
              if (item.path === "/callbacks" && user?.role === "user") return false;
              if (item.path === "/fax" && user?.role === "user") return false;
              if (item.path === "/history" && user?.role !== "super_admin" && user?.role !== "operator") return false;
              if (item.path === "/interpreter-requests" && !user?.bylyngo_access) return false;
              return true;
            })
            .map((item) => {
            const isActive = location.pathname === item.path;
            const showBadge = item.path === "/sms" && totalUnread > 0;
            const showImBadge = item.isIM && imUnread > 0;
            const showCommsBadge = item.path === "/phone" && (missedCalls + vmCount + totalUnread) > 0;
            const showTasksBadge = item.path === "/work" && (taskCounts.open > 0);
            const hasBadge = showBadge || showImBadge || showCommsBadge || showTasksBadge;
            const badgeCount = showBadge ? totalUnread : showImBadge ? imUnread : showCommsBadge ? (missedCalls + vmCount + totalUnread) : showTasksBadge ? taskCounts.open : 0;
            
            return (
              item.disabled ? (
                !collapsed && (
                <div
                  key={item.path}
                  className="flex items-center gap-3 px-6 py-3 text-sm font-bold text-white/30 cursor-not-allowed"
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5 text-white/20" strokeWidth={2} />
                  <span className="flex-1">{item.label}</span>
                  <span className="text-[9px] bg-white/10 text-white/40 px-1.5 py-0.5 rounded-full">Coming Soon</span>
                </div>
                )
              ) : (
              <NavLink
                key={item.path}
                to={item.path}
                title={collapsed ? item.label : undefined}
                className={cn(
                  "flex items-center transition-all duration-200 group relative",
                  collapsed ? "justify-center mx-1 px-0 py-2.5 rounded-lg" : "gap-3 py-3 text-sm font-bold",
                  // Nested items sit under their parent — smaller left
                  // indent + slightly muted font weight until active. Also
                  // uses a lighter font size so the parent Tools row still
                  // reads as the primary entry.
                  !collapsed && (item.nested ? "pl-10 pr-6 text-xs font-semibold" : "px-6"),
                  isActive
                    ? collapsed
                      ? "bg-navy-700 text-white"
                      : "bg-navy-700 text-white border-r-2 border-yellow-400"
                    : "text-white hover:bg-yellow-500/20 hover:text-yellow-300"
                )}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="relative">
                  {item.iconSrc ? (
                    // Custom PNG (Documents shield icon). Rendered at the
                    // same box size as lucide icons; sidebar background is
                    // navy so we don't tint white glyphs.
                    <img
                      src={item.iconSrc}
                      alt=""
                      aria-hidden="true"
                      className="w-5 h-5 object-contain"
                      draggable={false}
                    />
                  ) : item.rainbow === "dashboard" && !isActive ? (
                    <RainbowDashboardIcon className="w-5 h-5" />
                  ) : item.rainbow === "briefcase" && !isActive ? (
                    <RainbowBriefcaseIcon className="w-5 h-5" />
                  ) : (
                    <item.icon
                      className={cn(
                        "w-5 h-5 transition-colors",
                        isActive ? "text-yellow-400" : (item.color || "text-white")
                      )}
                      strokeWidth={2}
                    />
                  )}
                  {collapsed && hasBadge && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  )}
                  {item.path === "/phone" && (
                    <span
                      className={cn(
                        "absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-navy-600",
                        phoneReady ? "bg-green-400" : "bg-slate-400"
                      )}
                      title={phoneReady ? "Phone connected" : "Phone not connected"}
                      data-testid="nav-phone-status-dot"
                      data-ready={phoneReady ? "true" : "false"}
                    />
                  )}
                </div>
                {!collapsed && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    {showBadge && (
                      <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 min-w-[20px] justify-center animate-pulse">
                        {totalUnread}
                      </Badge>
                    )}
                    {showImBadge && (
                      <span className="relative flex items-center gap-1">
                        {imMentions > 0 && (
                          <span
                            className="relative inline-flex items-center justify-center min-w-[22px] h-[18px] px-1.5 text-[10px] font-extrabold rounded-full shadow-md"
                            style={{ backgroundColor: "#FFDA00", color: "#003366", border: "1.5px solid #003366" }}
                            title={`${imMentions} unread @mention${imMentions === 1 ? "" : "s"}`}
                            data-testid="im-mentions-badge"
                          >
                            <span className="relative z-10">@{imMentions}</span>
                            <span className="absolute inset-0 rounded-full animate-ping" style={{ backgroundColor: "rgba(255,218,0,0.55)" }} />
                          </span>
                        )}
                        <Badge className="text-[10px] px-2 py-0.5 min-w-[22px] justify-center font-bold shadow-md" style={{ backgroundColor: "#FFDA00", color: "#003366" }} data-testid="im-unread-badge">
                          {imUnread}
                        </Badge>
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 animate-ping" />
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500" />
                      </span>
                    )}
                    {!showImBadge && item.isIM && imMentions > 0 && (
                      <span
                        className="relative inline-flex items-center justify-center min-w-[22px] h-[18px] px-1.5 text-[10px] font-extrabold rounded-full shadow-md"
                        style={{ backgroundColor: "#FFDA00", color: "#003366", border: "1.5px solid #003366" }}
                        title={`${imMentions} unread @mention${imMentions === 1 ? "" : "s"}`}
                        data-testid="im-mentions-badge"
                      >
                        <span className="relative z-10">@{imMentions}</span>
                        <span className="absolute inset-0 rounded-full animate-ping" style={{ backgroundColor: "rgba(255,218,0,0.55)" }} />
                      </span>
                    )}
                    {showCommsBadge && (
                      <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 min-w-[20px] justify-center animate-pulse" data-testid="comms-badge">
                        {missedCalls + vmCount + totalUnread}
                      </Badge>
                    )}
                    {showTasksBadge && (
                      // Tasks badge: red when there are overdue tasks, blue when
                      // only open (not-yet-overdue) tasks exist. Shows total open.
                      <Badge
                        className={cn(
                          "text-white text-[10px] px-1.5 py-0.5 min-w-[20px] justify-center",
                          taskCounts.overdue > 0 ? "bg-red-500 animate-pulse" : "bg-blue-500"
                        )}
                        title={taskCounts.overdue > 0
                          ? `${taskCounts.overdue} overdue · ${taskCounts.open} open`
                          : `${taskCounts.open} open`}
                        data-testid="tasks-badge"
                      >
                        {taskCounts.open}
                      </Badge>
                    )}
                  </>
                )}
              </NavLink>
              )
            );
          })}

          {!simple && <KnowledgeBaseNavItem collapsed={collapsed} />}
          
          {/* Company Unread Indicators */}
          {!collapsed && !simple && totalUnread > 0 && companies.length > 0 && (
            <div className="mx-4 mt-4 p-3 bg-navy-700 rounded-xl space-y-2">
              <p className="text-[10px] font-semibold text-navy-200 uppercase tracking-wider px-2">
                Unread by Company
              </p>
              {companies.map((company) => {
                const key = company.name?.toLowerCase().includes("institute") ? "institute" 
                          : company.name?.toLowerCase().includes("avida") ? "avida" 
                          : "bylyngo";
                const theme = getCompanyTheme(company.name);
                const count = unreadCounts[key] || 0;
                
                if (count === 0) return null;
                
                return (
                  <NavLink
                    key={company.id}
                    to={`/sms?company=${key}`}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-navy-500 transition-colors"
                  >
                    {company.logo_url ? (
                      <img src={company.logo_url} alt={company.name} className="w-6 h-6 rounded-md object-contain bg-white/90 p-0.5" />
                    ) : (
                      <div className={cn("w-6 h-6 rounded-md flex items-center justify-center", theme.color)}>
                        <theme.icon className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <span className="text-xs font-medium text-navy-100 flex-1 truncate">
                      {company.name?.split(" ")[0]}
                    </span>
                    <Badge className={cn("text-[10px] px-1.5", theme.color, "text-white")}>
                      {count}
                    </Badge>
                  </NavLink>
                );
              })}
            </div>
          )}
          
          {/* Admin Section */}
          {["admin", "super_admin"].includes(user?.role) && (
            <>
              <div className={cn("my-4 border-t border-navy-500", collapsed ? "mx-2" : "mx-6")} />
              {adminNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    title={collapsed ? item.label : undefined}
                    className={cn(
                      "flex items-center transition-all duration-200 group",
                      collapsed ? "justify-center mx-1 px-0 py-2.5 rounded-lg" : "gap-3 px-6 py-3 text-sm font-bold",
                      isActive 
                        ? collapsed
                          ? "bg-navy-700 text-white"
                          : "bg-navy-700 text-white border-r-2 border-yellow-400" 
                        : "text-white hover:bg-yellow-500/20 hover:text-yellow-300"
                    )}
                    data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                  >
                    <item.icon className={cn(
                      "w-5 h-5 transition-colors",
                      isActive ? "text-yellow-400" : (item.color || "text-white")
                    )} strokeWidth={2} />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>

        {/* User Profile */}
        <div className={cn("border-t border-navy-500", collapsed ? "p-2 space-y-1" : "p-4 space-y-2")}>
          <NotificationBell collapsed={collapsed} />
          <HoldingCallersBadge collapsed={collapsed} />
          {collapsed ? (
            <NavLink to="/settings" title={`${user?.first_name} ${user?.last_name}`} className="flex justify-center p-1">
              <div className="relative">
                <Avatar className="w-9 h-9 border-2 border-navy-400">
                  {user?.avatar_url && <AvatarImage src={fileUrl(user.avatar_url)} alt={user?.first_name} />}
                  <AvatarFallback className="bg-navy-500 text-white text-xs font-medium">
                    {getInitials(user?.first_name, user?.last_name)}
                  </AvatarFallback>
                </Avatar>
                <span className={cn(
                  "absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-navy-600",
                  getStatusColor(user?.status)
                )} />
              </div>
            </NavLink>
          ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-yellow-500/20 transition-colors"
                data-testid="user-menu-trigger"
              >
                <div className="relative">
                  <Avatar className="w-10 h-10 border-2 border-navy-400">
                    {user?.avatar_url && <AvatarImage src={fileUrl(user.avatar_url)} alt={user?.first_name} />}
                    <AvatarFallback className="bg-navy-500 text-white text-sm font-medium">
                      {getInitials(user?.first_name, user?.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className={cn(
                    "absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white",
                    getStatusColor(user?.status)
                  )} />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-bold text-white truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-white/70 font-semibold capitalize">{user?.status || "Available"}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="end" 
              className="w-56 bg-white border-slate-200"
              data-testid="user-menu-content"
            >
              {/* Call Availability Toggle */}
              <div
                className="flex items-center justify-between px-2 py-2 cursor-pointer hover:bg-slate-50 rounded-sm"
                onClick={async () => {
                  try {
                    const res = await authAPI.toggleCallAvailability();
                    const newVal = res.data?.call_available;
                    // Refresh user data
                    const meRes = await authAPI.getMe();
                    if (meRes.data) {
                      // Update through auth context
                      window.dispatchEvent(new CustomEvent("refresh-user"));
                    }
                  } catch {}
                }}
                data-testid="call-availability-toggle"
              >
                <div className="flex items-center gap-2">
                  <PhoneOff className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-700">Available for Calls</span>
                </div>
                <div className={cn(
                  "w-8 h-4 rounded-full relative transition-colors",
                  user?.call_available !== false ? "bg-green-500" : "bg-slate-300"
                )}>
                  <div className={cn(
                    "w-3 h-3 rounded-full bg-white absolute top-0.5 transition-transform",
                    user?.call_available !== false ? "translate-x-4" : "translate-x-0.5"
                  )} />
                </div>
              </div>
              <DropdownMenuSeparator className="bg-slate-200" />
              <div className="px-2 py-1.5 text-sm text-slate-500">Set Status</div>
              {statusOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => updateStatus(option.value)}
                  className="hover:bg-slate-50 cursor-pointer"
                  data-testid={`status-${option.value}`}
                >
                  <span className={cn("w-2 h-2 rounded-full mr-2", option.color)} />
                  {option.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-slate-200" />
              <DropdownMenuItem 
                onClick={logout}
                className="text-red-600 hover:bg-red-50 hover:text-red-600 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </aside>
    </>
  );
};
